import { useMemo, useState } from 'react';
import type { PourCard } from '@/lib/apiClient';
import { Button } from '@/components';
import { pourStatus, pourStatusLabel, fmtDt } from './pourShared';
import { downloadCsv, logTableCss, toggle } from './PourLogTable';
import {
  DEFAULT_ROCK_STATUSES, ROCK_STATUSES, filterRockLog, fmtRockQty, parseRockQty, rockLogCsv, rockLogTotals, sortRockLog,
  type RockLogSort, type RockLogSortKey,
} from './rockLog';

/**
 * The Rock Log — every Rock/Aggregate delivery as one flat table anyone can
 * filter, sort, total and export. The Pour Log's sibling: same layout, same
 * read-only rule (office actions stay on the work queue), rows open the card
 * drawer. Totals are per unit (loads, tons, CY) because that's how rock is
 * ordered. The logic is in ./rockLog, where it's tested.
 */

export default function RockLogTable({ cards, showJob, fileName, onOpen }: {
  cards: readonly PourCard[];
  showJob: boolean;
  /** Without the extension, e.g. "rock-log-10796". */
  fileName: string;
  onOpen: (id: number) => void;
}) {
  const [search, setSearch] = useState('');
  const [statuses, setStatuses] = useState<readonly string[]>(DEFAULT_ROCK_STATUSES);
  const [from, setFrom] = useState('');
  const [to, setTo] = useState('');
  const [sort, setSort] = useState<RockLogSort>({ key: 'deliveryDate', dir: 1 });

  const rows = useMemo(
    () => sortRockLog(filterRockLog(cards, { search, statuses, from, to }), sort),
    [cards, search, statuses, from, to, sort],
  );
  const totals = useMemo(() => rockLogTotals(rows), [rows]);
  const isDefault = search === '' && from === '' && to === ''
    && statuses.length === DEFAULT_ROCK_STATUSES.length && DEFAULT_ROCK_STATUSES.every((s) => statuses.includes(s));
  const reset = () => { setSearch(''); setStatuses(DEFAULT_ROCK_STATUSES); setFrom(''); setTo(''); };

  const exportCsv = () => downloadCsv(rockLogCsv(rows), fileName);

  const th = (key: RockLogSortKey, label: string, opts: { num?: boolean; title?: string } = {}) => {
    const active = sort.key === key;
    return (
      <th className={opts.num ? 'pl-num' : undefined} title={opts.title}
          aria-sort={active ? (sort.dir === 1 ? 'ascending' : 'descending') : 'none'}>
        <button type="button"
                onClick={() => setSort((s) => (s.key === key ? { key, dir: s.dir === 1 ? -1 : 1 } : { key, dir: 1 }))}>
          {label}{active ? (sort.dir === 1 ? ' ↑' : ' ↓') : ''}
        </button>
      </th>
    );
  };
  const colCount = showJob ? 9 : 8;
  const totalLabel = totals.byUnit.map((t) => fmtRockQty(t.amount, t.unit)).join(' · ') || '—';

  return (
    <div>
      <style>{logTableCss}</style>

      {/* ── Filters ── */}
      <div className="pl-bar">
        <div className="pl-group">
          <span className="pl-lbl">Search</span>
          <input className="pl-input" style={{ width: 210 }} value={search} onChange={(e) => setSearch(e.target.value)}
                 placeholder={showJob ? 'Job, aggregate, supplier…' : 'Aggregate, location, supplier…'} aria-label="Search rock deliveries" />
        </div>
        <div className="pl-group">
          <span className="pl-lbl">Status</span>
          <div className="pl-toggles">
            {ROCK_STATUSES.map((s) => (
              <button key={s} type="button" className="pl-tog" aria-pressed={statuses.includes(s)}
                      onClick={() => setStatuses((l) => toggle(l, s))}>
                {pourStatusLabel(s, 'rock')}
              </button>
            ))}
          </div>
        </div>
        <div className="pl-group">
          <span className="pl-lbl">Delivery date</span>
          <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
            <input className="pl-input" type="date" value={from} max={to || undefined} onChange={(e) => setFrom(e.target.value)} aria-label="From date" />
            <span className="pl-muted" style={{ fontSize: 12 }}>to</span>
            <input className="pl-input" type="date" value={to} min={from || undefined} onChange={(e) => setTo(e.target.value)} aria-label="To date" />
          </div>
        </div>
        <div style={{ marginLeft: 'auto', display: 'flex', gap: 8, alignItems: 'center' }}>
          {!isDefault && (
            <button type="button" className="ew-btn-link" style={{ color: 'var(--ew-n-500)', font: 'var(--ew-fw-medium) 12px var(--ew-font)' }} onClick={reset}>
              Reset filters
            </button>
          )}
          <Button variant="secondary" size="sm" onClick={exportCsv} disabled={rows.length === 0}>
            Export CSV ({rows.length})
          </Button>
        </div>
      </div>

      {/* ── Table ── */}
      {rows.length === 0 ? (
        <div style={{ padding: 'var(--ew-s-5)', color: 'var(--ew-n-500)', font: 'var(--ew-fw-regular) var(--ew-fs-body-sm) var(--ew-font)' }}>
          No rock deliveries match these filters.
        </div>
      ) : (
        <div className="ew-table-scroll">
          <table className="pl-table">
            <thead>
              <tr>
                {showJob && th('job', 'Job')}
                {th('deliveryDate', 'Delivery Date')}
                {th('aggregate', 'Aggregate')}
                {th('qty', 'Quantity', { num: true, title: 'As the field ordered it — loads, tons or yards' })}
                {th('supplier', 'Supplier')}
                {th('costCode', 'Cost Code')}
                <th>Field Leader</th>
                {th('status', 'Status')}
                {th('source', 'Source')}
              </tr>
            </thead>
            <tbody>
              {rows.map((c) => {
                const st = pourStatus(c.status);
                const readable = parseRockQty(c.qtyText) != null;
                return (
                  <tr key={c.id} onClick={() => onOpen(c.id)}>
                    {showJob && (
                      <td>
                        <div style={{ fontWeight: 600 }}>{c.project?.jobNumber ?? c.jobNumberRaw ?? '—'}</div>
                        {c.project && <div className="pl-muted" style={{ fontSize: 12, maxWidth: 170, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{c.project.title}</div>}
                      </td>
                    )}
                    <td style={{ whiteSpace: 'nowrap', fontVariantNumeric: 'tabular-nums' }}>{fmtDt(c.pourDatetime)}</td>
                    <td>
                      <div style={{ fontWeight: 500 }}>{c.element ?? '—'}</div>
                      {c.location && <div className="pl-muted" style={{ fontSize: 12 }}>{c.location}</div>}
                    </td>
                    <td className="pl-num">
                      {!c.qtyText?.trim()
                        ? <span className="pl-muted">—</span>
                        : readable
                        ? c.qtyText
                        : <span style={{ color: 'var(--ew-safety-700)' }} title="Not one amount and a unit, so it isn&rsquo;t in the totals">“{c.qtyText}”</span>}
                    </td>
                    <td className="pl-muted">{c.vendor ?? '—'}</td>
                    <td className="pl-muted" style={{ whiteSpace: 'nowrap' }}>{c.costCode ?? '—'}</td>
                    <td className="pl-muted">{c.superName ?? '—'}</td>
                    <td style={{ whiteSpace: 'nowrap', color: st.color }}>{pourStatusLabel(c.status, c.pourType)}</td>
                    <td className="pl-muted" style={{ whiteSpace: 'nowrap' }}>{c.source === 'manual' ? 'Office entry' : 'Field form'}</td>
                  </tr>
                );
              })}
            </tbody>
            <tfoot>
              <tr>
                <td colSpan={showJob ? 3 : 2}>Total · {totals.rows} deliver{totals.rows === 1 ? 'y' : 'ies'}</td>
                <td className="pl-num">{totalLabel}</td>
                <td colSpan={colCount - (showJob ? 4 : 3)} />
              </tr>
            </tfoot>
          </table>
        </div>
      )}

      {/* What the totals do and don't include — so nobody quotes a short number. */}
      <div style={{ padding: '10px var(--ew-s-4)', borderTop: '1px solid var(--ew-n-100)', font: 'var(--ew-fw-regular) 12px/1.5 var(--ew-font)', color: 'var(--ew-n-500)' }}>
        Totals cover the rows shown, added up per unit as the field ordered them — loads and tons aren't converted into each other.
        {totals.unreadableQty > 0 && <> <span style={{ color: 'var(--ew-safety-700)' }}>{totals.unreadableQty} {totals.unreadableQty === 1 ? 'has a quantity' : 'have quantities'} without a clear amount and unit, so {totals.unreadableQty === 1 ? 'it isn’t' : 'they aren’t'} in the totals.</span></>}
        {!statuses.includes('cancelled') && <> Cancelled orders are left out by default.</>}
      </div>
    </div>
  );
}
